import React, { useEffect, useRef } from 'react'
import ApexCharts, { ApexOptions } from 'apexcharts'
import { getCSSVariableValue } from '../../../assets/ts/_utils'
import { useThemeMode } from '../../layout/theme-mode/ThemeModeProvider'

type Props = {
    className: string
    chartColor: string
    chartHeight: string
    title: string
    subtitle: string
    amount: string
    chartData?: { [key: string]: number }
    seriesName?: string
    itemImage?: string
    blockedQuantity?: number
}

const MixedWidgetForInventory: React.FC<Props> = ({
    className,
    chartColor,
    chartHeight,
    title,
    subtitle,
    amount,
    chartData,
    seriesName = 'Quantity',
    itemImage,
    blockedQuantity
}) => {
    const chartRef = useRef<HTMLDivElement | null>(null)
    const { mode } = useThemeMode()

    // Process chart data
    const processedChartData = React.useMemo(() => {
        if (!chartData) {
            return {
                categories: ['Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul'],
                data: [15, 25, 15, 40, 20, 50]
            }
        }

        const entries = Object.entries(chartData)
        const categories = entries.map(([key]) => {
            // Parse DD/MM/YYYY format correctly
            const [day, month, year] = key.split('/')
            const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day))
            return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
        })

        const data = entries.map(([, value]) => Math.round(value * 100) / 100)

        return { categories, data }
    }, [chartData])

    const refreshChart = () => {
        if (!chartRef.current) {
            return
        }

        const chart = new ApexCharts(
            chartRef.current,
            chartOptions(chartColor, chartHeight, processedChartData, seriesName)
        )
        if (chart) {
            chart.render()
        }

        return chart
    }

    useEffect(() => {
        const chart = refreshChart()

        return () => {
            if (chart) {
                chart.destroy()
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chartRef, mode, processedChartData])

    return (
        <div className={`card ${className}`}>
            {/* begin::Body */}
            <div className='card-body d-flex flex-column p-0'>
                {/* begin::Stats */}
                <div className='flex-grow-1 card-p pb-0'>
                    <div className='d-flex flex-stack flex-wrap'>
                        <div className='d-flex align-items-center'>
                            {itemImage && (
                                <img
                                    src={itemImage}
                                    alt={title}
                                    style={{
                                        width: '40px',
                                        height: '40px',
                                        borderRadius: '50%',
                                        objectFit: 'cover',
                                        marginRight: '15px'
                                    }}
                                    onError={(e) => {
                                        (e.target as HTMLImageElement).style.display = 'none'
                                    }}
                                />
                            )}
                            <div>
                                <a href='#' className='text-dark text-hover-primary fw-bold fs-4'>
                                    {title}
                                </a>
                                <div className='text-dark fs-8 fw-semibold'>{subtitle}</div>
                            </div>
                        </div>
                        {/* <div className={`fw-bold fs-2 text-${chartColor}`}>{amount}</div> */}
                        <div className='d-flex flex-column align-items-end'>
                            <div className={`fw-bold fs-4 text-${chartColor}`}>{amount}</div>
                            <div style={{
                                backgroundColor: '#f5f5f5',
                                padding: '4px 7px',
                                borderRadius: '6px',
                                fontSize: '0.875rem',
                                color: '#666',
                                marginTop: '4px',
                                display: 'inline-block'
                            }}>
                                {blockedQuantity && blockedQuantity > 0 ? blockedQuantity.toFixed(2) : '0.00'}
                            </div>
                        </div>
                    </div>
                </div>
                {/* end::Stats */}

                {/* begin::Chart */}
                <div ref={chartRef} className='mixed-widget-7-chart card-rounded-bottom'></div>
                {/* end::Chart */}
            </div>
            {/* end::Body */}
        </div>
    )
}

const chartOptions = (
    chartColor: string,
    chartHeight: string,
    processedData: { categories: string[], data: number[] },
    seriesName: string
): ApexOptions => {
    const labelColor = getCSSVariableValue('--bs-gray-800')
    const strokeColor = getCSSVariableValue('--bs-gray-300')
    const baseColor = getCSSVariableValue('--bs-' + chartColor)
    const lightColor = getCSSVariableValue('--bs-' + chartColor + '-light')

    // Calculate max value for y-axis
    const maxValue = Math.max(...processedData.data)
    const yAxisMax = Math.ceil(maxValue * 1.1) // Add 10% padding

    return {
        series: [
            {
                name: seriesName,
                data: processedData.data,
            },
        ],
        chart: {
            fontFamily: 'inherit',
            type: 'area',
            height: chartHeight,
            toolbar: {
                show: false,
            },
            zoom: {
                enabled: false,
            },
            sparkline: {
                enabled: true,
            },
        },
        plotOptions: {},
        legend: {
            show: false,
        },
        dataLabels: {
            enabled: false,
        },
        fill: {
            type: 'solid',
            opacity: 1,
        },
        stroke: {
            curve: 'smooth',
            show: true,
            width: 3,
            colors: [baseColor],
        },
        xaxis: {
            categories: processedData.categories,
            axisBorder: {
                show: false,
            },
            axisTicks: {
                show: false,
            },
            labels: {
                show: false,
                style: {
                    colors: labelColor,
                    fontSize: '12px',
                },
            },
            crosshairs: {
                show: false,
                position: 'front',
                stroke: {
                    color: strokeColor,
                    width: 1,
                    dashArray: 3,
                },
            },
            tooltip: {
                enabled: false,
            },
        },
        yaxis: {
            min: 0,
            max: yAxisMax,
            labels: {
                show: false,
                style: {
                    colors: labelColor,
                    fontSize: '12px',
                },
            },
        },
        states: {
            normal: {
                filter: {
                    type: 'none',
                    value: 0,
                },
            },
            hover: {
                filter: {
                    type: 'none',
                    value: 0,
                },
            },
            active: {
                allowMultipleDataPointsSelection: false,
                filter: {
                    type: 'none',
                    value: 0,
                },
            },
        },
        tooltip: {
            style: {
                fontSize: '12px',
            },
            y: {
                formatter: function (val) {
                    return val.toLocaleString()
                },
            },
        },
        colors: [lightColor],
        markers: {
            colors: [lightColor],
            strokeColors: [baseColor],
            strokeWidth: 3,
        },
    }
}

export { MixedWidgetForInventory }
